import { forwardRef, useEffect, useRef } from 'react';

interface VenereTextareaProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  variant?: 'default' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  required?: boolean;
  className?: string;
  error?: string;
  helperText?: string;
  maxLength?: number;
  minRows?: number;
  maxRows?: number;
  autoResize?: boolean;
  onKeyPress?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export const VenereTextarea = forwardRef<HTMLTextAreaElement, VenereTextareaProps>(({
  label,
  placeholder,
  value,
  onChange,
  variant = 'default',
  size = 'md',
  disabled = false,
  required = false,
  className = '',
  error,
  helperText,
  maxLength,
  minRows = 1,
  maxRows = 4,
  autoResize = true,
  onKeyPress,
}, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Handle both internal ref and forwarded ref
  useEffect(() => {
    if (textareaRef.current && typeof ref === 'function') {
      ref(textareaRef.current);
    } else if (textareaRef.current && ref) {
      (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = textareaRef.current;
    }
  }, [ref]);

  const baseClasses = 'w-full rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed resize-none overflow-y-auto min-h-0';
  
  const variantClasses = {
    default: 'venere-text-secondary border-[var(--venere-slate)] focus:border-[var(--venere-royal)] focus:ring-[var(--venere-royal)] placeholder:text-[var(--venere-silver)]',
    error: 'border-[var(--venere-error)] focus:border-[var(--venere-error)] focus:ring-[var(--venere-error)] placeholder:text-[var(--venere-navy)]',
    success: 'border-[var(--venere-success)] focus:border-[var(--venere-success)] focus:ring-[var(--venere-success)] placeholder:text-[var(--venere-navy)]',
  };

  const sizeClasses = {
    sm: 'px-3 py-3 text-xs',
    md: 'px-4 py-3 text-sm',
    lg: 'px-4 py-4 text-base',
  };

  const inputVariant = error ? 'error' : variant;

  // Auto-resize functionality
  const handleResize = () => {
    if (!autoResize || !textareaRef.current) return;
    
    const textarea = textareaRef.current;
    textarea.style.height = 'auto';
    
    const lineHeight = 20; // Approximate line height in pixels
    const minHeight = lineHeight * minRows;
    const maxHeight = lineHeight * maxRows;
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    
    textarea.style.height = `${newHeight}px`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e.target.value);
    if (autoResize) {
      handleResize();
    }
  };

  useEffect(() => {
    if (autoResize) {
      handleResize();
    }
  }, [value, autoResize]);

  return (
    <div className={`h-full flex flex-col ${className}`}>
      {label && (
        <label className="block venere-text-primary mb-1">
          {label}
          {required && <span className="text-[var(--venere-error)] ml-1">*</span>}
        </label>
      )}
      
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyPress={onKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        rows={minRows}
        className={`flex-1 ${baseClasses} ${variantClasses[inputVariant]} ${sizeClasses[size]}`}
      />
      
      {(error || helperText) && (
        <p className={`text-sm mt-1 ${error ? 'text-[var(--venere-error)]' : 'text-[var(--venere-slate)]'}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

VenereTextarea.displayName = 'VenereTextarea';

export default VenereTextarea; 